import type { defineBrowserModule, ScreenDeclaration } from "@amtana/browser";
import { POSTS } from "virtual:warike-posts";
import type { Post, PostFile } from "virtual:warike-posts";

import {
  ALL_TAGS,
  LISTED_POSTS,
  newestPostDate,
  postChanged,
  postPath,
  postsWithTag,
  tagPath,
} from "./behavior/warike-posts.ts";
import { machineLinks } from "./model/machine-links.ts";
import { WARIKE_WORDS } from "./model/warike-words.ts";
import { postJsonLd, tagJsonLd } from "./post-head.web.ts";

// The blog's screens. The index is a literal screen `screen-seo` reads. A post's and a tag's screen
// come from the manifest, so the frontmatter schema, the audit and `warike-parity.json` hold their heads.

const LANGUAGES = ["en"] as const;

function postScreen(post: Post): ScreenDeclaration {
  const file = post.languages.en as PostFile;
  const path = postPath(post.slug);
  return {
    path,
    languages: LANGUAGES,
    title: file.seoTitle,
    seo: {
      description: file.description,
      keyphrase: file.keyphrase ?? "",
      keywords: file.tags,
      contentType: "article",
      type: "article",
      lastmod: postChanged(file),
      alternateLinks: () => machineLinks(path),
      jsonLd: () => postJsonLd(post.slug, file),
      sources: [file.file],
    },
    load: async () => {
      const { postScreenFor } = await import("./ui/sections/post-screen.tsx");
      return { default: await postScreenFor(post) };
    },
  };
}

function tagScreen(tag: string): ScreenDeclaration {
  const path = tagPath(tag);
  return {
    path,
    languages: LANGUAGES,
    title: WARIKE_WORDS.blog.tagTitle(tag),
    seo: {
      description: WARIKE_WORDS.blog.tagDescription(tag),
      keywords: [tag],
      contentType: "landing",
      lastmod: newestPostDate(postsWithTag(tag)),
      alternateLinks: () => machineLinks(path),
      jsonLd: () => tagJsonLd(tag, postsWithTag(tag)),
    },
    load: async () => {
      const { tagScreenFor } = await import("./ui/sections/tag-screen.tsx");
      return { default: tagScreenFor(tag) };
    },
  };
}

const postScreens = Object.fromEntries(POSTS.map((post) => [`post-${post.slug}`, postScreen(post)]));
const tagScreens = Object.fromEntries(ALL_TAGS.map((tag) => [`tag-${tag.name}`, tagScreen(tag.name)]));

/** The index stays a literal `.withScreens({ ... })`, since `screen-seo` reads this file as text. */
export function withBlogScreens(site: ReturnType<typeof defineBrowserModule>) {
  return site
    .withScreens({
      blog: {
        path: "/blog",
        languages: LANGUAGES,
        title: WARIKE_WORDS.blog.title,
        seo: {
          description: WARIKE_WORDS.blog.description,
          contentType: "landing",
          lastmod: newestPostDate(LISTED_POSTS),
          alternateLinks: () => machineLinks("/blog"),
        },
        load: () => import("./ui/sections/blog-screen.tsx"),
      },
    })
    .withScreens(postScreens)
    .withScreens(tagScreens);
}
